import { useRef, useState } from 'react';
import { Upload, X, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.82;
const DEFAULT_MAX_IMAGES = 999; // effectively unlimited
const MAX_FILE_SIZE_MB = 25; // hard cap on raw file before compression

/**
 * Resize + compress an image file client-side and return a JPEG Blob.
 */
async function compressImage(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });

  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_WIDTH / img.width);
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('Compression failed'))),
        'image/jpeg',
        JPEG_QUALITY,
      );
    };
    img.onerror = () => reject(new Error('Could not process image'));
    img.src = dataUrl;
  });
}

export default function ImageUploader({ value = [], onChange, maxImages = DEFAULT_MAX_IMAGES, singleMode = false }) {
  const MAX_IMAGES = singleMode ? 1 : maxImages;
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const remaining = singleMode ? 1 : MAX_IMAGES - value.length;
    if (remaining <= 0) {
      toast.error(`You can upload up to ${MAX_IMAGES} images`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error(`${file.name} is too large (max ${MAX_FILE_SIZE_MB} MB)`);
          continue;
        }
        try {
          const blob = await compressImage(file);
          const fd = new FormData();
          fd.append('file', blob, 'photo.jpg');
          const res = await api.post('/upload/image', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (res.data?.url) newUrls.push(res.data.url);
        } catch (e) {
          const detail = e?.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : `Could not upload ${file.name}`);
        }
      }
      if (newUrls.length > 0) {
        if (singleMode) {
          onChange([newUrls[0]]);
        } else {
          onChange([...value, ...newUrls]);
        }
        toast.success(`${newUrls.length} photo${newUrls.length > 1 ? 's' : ''} uploaded`);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const moveFirst = (idx) => {
    if (idx === 0) return;
    const arr = [...value];
    const [pick] = arr.splice(idx, 1);
    arr.unshift(pick);
    onChange(arr);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="image-uploader">
      {/* Hidden inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid="image-file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFiles(e.target.files)}
        className="hidden"
        data-testid="image-camera-input"
      />

      {/* Upload buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || value.length >= MAX_IMAGES}
          data-testid="upload-from-gallery"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[color:var(--mb-border)] bg-[color:var(--mb-bg)] p-6 hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ transition: 'border-color 200ms ease, color 200ms ease' }}
        >
          {uploading ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
          <span className="text-[0.7rem] tracking-[0.25em] uppercase text-center leading-tight">From Gallery</span>
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={uploading || value.length >= MAX_IMAGES}
          data-testid="upload-from-camera"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-[color:var(--mb-border)] bg-[color:var(--mb-bg)] p-6 hover:border-[color:var(--mb-primary)] hover:text-[color:var(--mb-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ transition: 'border-color 200ms ease, color 200ms ease' }}
        >
          {uploading ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
          <span className="text-[0.7rem] tracking-[0.25em] uppercase text-center leading-tight">Take Photo</span>
        </button>
      </div>

      <p className="text-xs text-[color:var(--mb-text-muted)]">
        {value.length} photo{value.length === 1 ? '' : 's'} · auto-compressed for fast loading.{!singleMode && value.length > 1 && ' Tap "Set cover" to change the main photo.'}
      </p>

      {value.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="image-previews">
          {value.map((src, i) => (
            <div
              key={i}
              className={`relative aspect-square overflow-hidden bg-[color:var(--mb-bg)] border ${i === 0 ? 'border-[color:var(--mb-primary)]' : 'border-[color:var(--mb-border)]'}`}
              data-testid={`image-preview-${i}`}
            >
              <img src={resolveImageUrl(src)} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 bg-[color:var(--mb-primary)] text-white text-[0.55rem] tracking-[0.2em] uppercase px-1.5 py-0.5">
                  Cover
                </span>
              )}
              {i !== 0 && (
                <button
                  type="button"
                  onClick={() => moveFirst(i)}
                  data-testid={`image-set-cover-${i}`}
                  className="absolute top-1 left-1 bg-black/60 text-white text-[0.55rem] tracking-[0.2em] uppercase px-1.5 py-0.5 hover:bg-[color:var(--mb-primary)]"
                  style={{ transition: 'background-color 200ms ease' }}
                >
                  Set cover
                </button>
              )}
              <button
                type="button"
                onClick={() => removeAt(i)}
                data-testid={`image-remove-${i}`}
                aria-label="Remove image"
                className="absolute top-1 right-1 bg-black/70 text-white w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-500"
                style={{ transition: 'background-color 200ms ease' }}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
